import {
  Checkbox,
  FormControlLabel,
  FormHelperText,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import type { InvestorPortalRoleWithNoneValue } from "../../../../api/types/investorContactsTypes";
import useSettingsContext from "../../useSettingsContext";
import PortalRoleSelect from "./PortalRoleSelect";

interface Props<TRole extends InvestorPortalRoleWithNoneValue> {
  role: TRole | "";
  roleDisabled?: boolean;
  roleOptions: TRole[];
  selectedCategoryExternalIds: string[];
  onRoleChange: (role: TRole) => void;
  onToggleCategory: (categoryExternalId: string) => void;
  onSetAllCategories: (allCategoryExternalIds: string[], selected: boolean) => void;
}

const PermissionsFields = <TRole extends InvestorPortalRoleWithNoneValue>({
  role,
  roleDisabled,
  roleOptions,
  selectedCategoryExternalIds,
  onRoleChange,
  onToggleCategory,
  onSetAllCategories,
}: Props<TRole>) => {
  const { accessCategories } = useSettingsContext();

  const allCategoryExternalIds = accessCategories.map((category) => category.externalId);
  const allCategoriesSelected =
    accessCategories.length > 0 &&
    allCategoryExternalIds.every((externalId) => selectedCategoryExternalIds.includes(externalId));

  return (
    <Stack spacing={2.5}>
      <Stack spacing={2}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Permission information</Typography>
          <Typography color="textSecondary">
            Specify the role and select the categories of documents you want to access.
          </Typography>
        </Stack>
        <PortalRoleSelect
          fullWidth
          disabled={roleDisabled}
          role={role}
          onChange={onRoleChange}
          roleOptions={roleOptions}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={allCategoriesSelected}
              indeterminate={!allCategoriesSelected && selectedCategoryExternalIds.length > 0}
              onChange={(_, checked) => onSetAllCategories(allCategoryExternalIds, checked)}
              tabIndex={-1}
              sx={{ pl: 0 }}
            />
          }
          label={<Typography>All categories</Typography>}
        />
      </Stack>
      <Stack spacing={1}>
        <Stack spacing={0.5}>
          <Typography variant="subtitle2">Documents</Typography>
          <Typography color="textSecondary">
            Controls which documents and email notifications this member can receive and view in the portal inbox.
          </Typography>
        </Stack>
        <List>
          {accessCategories.map((category) => (
            <ListItem key={category.id} dense disablePadding>
              <ListItemButton role={undefined} onClick={() => onToggleCategory(category.externalId)} dense>
                <ListItemIcon sx={{ minWidth: 0 }}>
                  <Checkbox
                    edge="start"
                    tabIndex={-1}
                    disableRipple
                    checked={selectedCategoryExternalIds.includes(category.externalId)}
                  />
                </ListItemIcon>
                <ListItemText>
                  <Tooltip title={category.description ?? ""} placement="top-start" arrow describeChild>
                    <Typography component="span">{category.name}</Typography>
                  </Tooltip>
                </ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {accessCategories.length > 0 && selectedCategoryExternalIds.length === 0 && (
          <FormHelperText error>Select at least one category.</FormHelperText>
        )}
      </Stack>
    </Stack>
  );
};

export default PermissionsFields;
